import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { jsonFromArray, PUBLIC_TUTORIALS_HUB_KEY } from "../../utility";
import UserAvatar from "../management/UserAvatar";
import ShareDialogGroupHeader, {
    buildGroupCoverage,
    resolveGroupMembers,
    ShareDialogAvailableGroupRow,
    ShareDialogGroupIcon,
    ShareDialogGroupMembersDialog,
    ShareDialogGroupUserRow,
    ShareDialogPanelHeading,
    ShareDialogUserListLoading,
    ShareDialogUserSectionHeader,
    shareDialogSearchSx,
} from "../ShareDialogGroupHeader";
import ShareDialogDeliveryOptions from "../ShareDialogDeliveryOptions";
import ShareDialogDeliverySummary from "../ShareDialogDeliverySummary";
import ShareDialogSilentShareConfirm from "../ShareDialogSilentShareConfirm";
import {
    buildShareFeedbackMessage,
    sendSharedItemEmails,
} from "../shareDialogDelivery";
import useShareDialogUsersTabLoading from "../useShareDialogUsersTabLoading";

const surfaceSx = {
    borderRadius: 2.5,
    borderColor: "divider",
    overflow: "hidden",
};

function PermissionSwitch({ checked, label, onChange }) {
    return (
        <Box
            component="label"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                color: "text.secondary",
                whiteSpace: "nowrap",
            }}
        >
            <Switch
                size="small"
                checked={checked}
                onChange={onChange}
                inputProps={{ "aria-label": label }}
            />
            <Typography variant="caption">{label.split(": ").pop()}</Typography>
        </Box>
    );
}

function AccessRow({
    avatar,
    canEdit,
    canViewResults,
    onChangePermission,
    onRemove,
    onViewMembers,
    secondary,
    title,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 1.25, md: 2 },
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
            }}
        >
            <Box
                // Only the name side of the row opens the member list, so the
                // permission switches keep their own click targets
                onClick={onViewMembers}
                onKeyDown={
                    onViewMembers
                        ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  onViewMembers();
                              }
                          }
                        : undefined
                }
                role={onViewMembers ? "button" : undefined}
                tabIndex={onViewMembers ? 0 : undefined}
                aria-label={
                    onViewMembers
                        ? `View the members of ${title}`
                        : undefined
                }
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                    ...(onViewMembers
                        ? {
                              cursor: "pointer",
                              borderRadius: 1.5,
                              "&:hover": {
                                  backgroundColor: "action.hover",
                              },
                          }
                        : {}),
                }}
            >
                {avatar}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 800 }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {secondary}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "space-between", md: "flex-end" },
                    flexWrap: "wrap",
                    gap: 0.5,
                    pl: { xs: 5.75, md: 0 },
                }}
            >
                <PermissionSwitch
                    checked={canEdit}
                    label={`${title}: Can edit questions`}
                    onChange={() => onChangePermission("canEdit")}
                />
                <PermissionSwitch
                    checked={canViewResults}
                    label={`${title}: Can view answers`}
                    onChange={() => onChangePermission("canViewResults")}
                />
                <IconButton
                    size="small"
                    aria-label={`Remove ${title}`}
                    onClick={onRemove}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}

function escapeHtml(value) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

export default function ShareSurveyDialog(props) {
    const { enqueueSnackbar } = useSnackbar();
    const {
        folders = [],
        foldersLoaded = false,
        groups = [],
        groupsLoaded = false,
        users = [],
    } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [availableHubs, setAvailableHubs] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [selectedUserFilter, setSelectedUserFilter] = useState("");
    const [selectedGroupFilter, setSelectedGroupFilter] = useState("");
    const [availableGroupFilter, setAvailableGroupFilter] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    // The { group, hub } whose members are being viewed, or null
    const [viewingGroup, setViewingGroup] = useState(null);
    const { beginUsersTabLoading, usersTabLoading } =
        useShareDialogUsersTabLoading(activeTab);
    // In-platform notifications are the expected outcome of sharing, so they
    // start on and have to be deliberately turned off.
    const [sendNotifications, setSendNotifications] = useState(true);
    const [sendEmails, setSendEmails] = useState(false);
    const [embargoNotifications, setEmbargoNotifications] = useState(false);
    const messageRef = useRef("");
    const deliveryOptionsRef = useRef(null);
    const [fromDate, setFromDate] = useState(new Date());
    const [silentShareConfirmOpen, setSilentShareConfirmOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const isLoading =
        !currentUserData ||
        !props.surveyInfo ||
        !foldersLoaded ||
        !groupsLoaded;

    const userShouldBeVisible = (user) => {
        if (!currentUserData || user.key === currentUserData.key) {
            return false;
        }

        return (
            (user.admins || []).includes(currentUserData.key) ||
            user.createdBy === currentUserData.key ||
            currentUserData.role === "System Digital Manager"
        );
    };

    useEffect(() => {
        if (!props.open || isLoading) {
            return;
        }

        const surveyUsers = new Map(
            (props.surveyInfo.users || []).map((user) => [user.key, user])
        );
        const surveyGroups = new Map(
            (props.surveyInfo.groups || []).map((group) => [group.key, group])
        );

        setSelectedUsers(
            users
                .filter((user) => userShouldBeVisible(user))
                .filter((user) => surveyUsers.has(user.key))
                .map((user) => ({
                    ...user,
                    canEdit: Boolean(surveyUsers.get(user.key)?.canEdit),
                    canViewResults: Boolean(
                        surveyUsers.get(user.key)?.canViewResults
                    ),
                }))
        );

        const accessibleHubs = folders
            .filter(
                (folder) =>
                    folder.hub &&
                    ((folder.managers || []).includes(currentUserData.key) ||
                        (folder.users || []).includes(currentUserData.key))
            )
            .map((hub) => ({
                ...hub,
                groups: groups
                    .filter((group) => group.hub === hub.key)
                    .map((group) => ({
                        ...group,
                        hubName: hub.name,
                        canEdit: Boolean(surveyGroups.get(group.key)?.canEdit),
                        canViewResults: Boolean(
                            surveyGroups.get(group.key)?.canViewResults
                        ),
                    })),
            }));

        setAvailableHubs(accessibleHubs);
        setSelectedGroups(
            accessibleHubs
                .flatMap((hub) => hub.groups)
                .filter((group) => surveyGroups.has(group.key))
        );
        setUserSearch("");
        setSelectedUserFilter("");
        setSelectedGroupFilter("");
        setAvailableGroupFilter("");
        setActiveTab(0);
        setViewingGroup(null);
        setSendNotifications(true);
        setSendEmails(false);
        setEmbargoNotifications(false);
        setSilentShareConfirmOpen(false);
        messageRef.current = "";
        setFromDate(new Date());
        setIsSaving(false);
        setSaveError("");
        // The visibility rules intentionally mirror the existing sharing flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folders, groups, isLoading, props.open, props.surveyInfo, users]);

    const groupCoverage = useMemo(
        () =>
            buildGroupCoverage({
                getGroupName: (group) => group.name,
                selectedGroups,
                users,
            }),
        [selectedGroups, users]
    );
    const groupMemberKeys = groupCoverage.memberKeys;

    const viewedGroupMembers = resolveGroupMembers(viewingGroup?.group, users);

    const handleViewGroupMembers = (group, hub) => () => {
        setViewingGroup({ group, hub });
    };

    const handleGroupMembersClose = () => {
        setViewingGroup(null);
    };

    const directlySelectedUsers = useMemo(
        () => selectedUsers.filter((user) => !groupMemberKeys.has(user.key)),
        [groupMemberKeys, selectedUsers]
    );

    const recipients = useMemo(() => {
        const recipientByKey = new Map();

        selectedUsers.forEach((user) => recipientByKey.set(user.key, user));
        groupMemberKeys.forEach((key) => {
            const matchingUser = users.find((user) => user.key === key);
            if (matchingUser) {
                recipientByKey.set(key, matchingUser);
            }
        });
        recipientByKey.delete(currentUserData?.key);

        return Array.from(recipientByKey.values());
    }, [currentUserData, groupMemberKeys, selectedUsers, users]);

    const shouldPrepareUserLists = activeTab === 1 && !usersTabLoading;

    const availableUsers = useMemo(
        () =>
            shouldPrepareUserLists
                ? users
                      .filter((user) => userShouldBeVisible(user))
                      .filter(
                          (user) =>
                              !selectedUsers.some(
                                  (selectedUser) =>
                                      selectedUser.key === user.key
                              )
                      )
                      .filter((user) =>
                          (user.fullName || "")
                              .toLowerCase()
                              .includes(userSearch.trim().toLowerCase())
                      )
                      .sort((first, second) =>
                          (first.fullName || "").localeCompare(
                              second.fullName || ""
                          )
                      )
                : [],
        // userShouldBeVisible is deliberately derived from the current context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            currentUserData,
            selectedUsers,
            shouldPrepareUserLists,
            userSearch,
            users,
        ]
    );

    const sortedHubs = useMemo(
        () =>
            [...availableHubs].sort(
                (first, second) =>
                    (first.ancestors?.length || 0) -
                    (second.ancestors?.length || 0)
            ),
        [availableHubs]
    );

    const getHubBreadcrumb = (hub) => {
        if (!hub) return "Hub unavailable";

        const ancestorNames = (hub.ancestors || [])
            .map((ancestorKey) =>
                folders.find((folder) => folder.key === ancestorKey)
            )
            .filter(Boolean)
            .sort(
                (first, second) =>
                    (first.ancestors?.length || 0) -
                    (second.ancestors?.length || 0)
            )
            .map((ancestorHub) => ancestorHub.name);

        return [...ancestorNames, hub.name].join(" › ");
    };

    const selectedGroupOptions = selectedGroups
        .map((group) => ({
            group,
            hub: sortedHubs.find((hub) =>
                hub.groups.some((hubGroup) => hubGroup.key === group.key)
            ),
        }))
        .filter(({ group, hub }) =>
            `${group.name} ${getHubBreadcrumb(hub)}`
                .toLowerCase()
                .includes(selectedGroupFilter.trim().toLowerCase())
        )
        .sort((first, second) => {
            const hubComparison = getHubBreadcrumb(first.hub).localeCompare(
                getHubBreadcrumb(second.hub)
            );
            return (
                hubComparison ||
                first.group.name.localeCompare(second.group.name)
            );
        });

    const filteredAvailableHubs = sortedHubs
        .filter((hub) => hub.key !== PUBLIC_TUTORIALS_HUB_KEY)
        .map((hub) => ({
            ...hub,
            groups: hub.groups
                .filter(
                    (group) =>
                        !selectedGroups.some(
                            (selectedGroup) => selectedGroup.key === group.key
                        ) &&
                        `${group.name} ${getHubBreadcrumb(hub)}`
                            .toLowerCase()
                            .includes(availableGroupFilter.trim().toLowerCase())
                )
                .sort((first, second) => first.name.localeCompare(second.name)),
        }))
        .filter((hub) => hub.groups.length > 0);

    const filteredSelectedUsers = shouldPrepareUserLists
        ? directlySelectedUsers.filter((user) =>
              (user.fullName || "")
                  .toLowerCase()
                  .includes(selectedUserFilter.trim().toLowerCase())
          )
        : [];

    const filteredGroupSelectedUsers = shouldPrepareUserLists
        ? groupCoverage.members.filter((user) =>
              (user.fullName || "")
                  .toLowerCase()
                  .includes(selectedUserFilter.trim().toLowerCase())
          )
        : [];

    const selectedUserSections = [
        {
            key: "individual",
            label: "Added individually",
            icon: <PersonOutlineIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredSelectedUsers,
            viaGroup: false,
        },
        {
            key: "viaGroups",
            label: "Included by a group",
            icon: <GroupsOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredGroupSelectedUsers,
            viaGroup: true,
        },
    ].filter((section) => section.users.length > 0);

    // With only one section on show its header would just repeat the panel title
    const showSelectedUserSectionHeaders = selectedUserSections.length > 1;

    const handleTabChange = (event, value) => {
        if (value === 1 && activeTab !== 1) {
            beginUsersTabLoading();
        }
        setActiveTab(value);
    };

    function handleReviewDeliveryOptions() {
        deliveryOptionsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }

    function addUser(user) {
        setSelectedUsers((current) => [
            ...current,
            { ...user, canEdit: false, canViewResults: false },
        ]);
        setUserSearch("");
    }

    function addGroup(group) {
        setSelectedGroups((current) => [
            ...current,
            { ...group, canEdit: false, canViewResults: false },
        ]);
    }

    function updateUserPermission(userKey, permission) {
        setSelectedUsers((current) =>
            current.map((user) =>
                user.key === userKey
                    ? { ...user, [permission]: !user[permission] }
                    : user
            )
        );
    }

    function updateGroupPermission(groupKey, permission) {
        setSelectedGroups((current) =>
            current.map((group) =>
                group.key === groupKey
                    ? { ...group, [permission]: !group[permission] }
                    : group
            )
        );
    }

    function handleDeliveryChange(event) {
        const { checked, name } = event.target;

        if (name === "notification") {
            setSendNotifications(checked);
        } else if (name === "email") {
            setSendEmails(checked);
        } else if (name === "embargo") {
            setEmbargoNotifications(checked);
            setFromDate(new Date());
        }

        if (
            (name === "notification" && !checked && !sendEmails) ||
            (name === "email" && !checked && !sendNotifications)
        ) {
            setEmbargoNotifications(false);
            setFromDate(new Date());
        }
    }

    function handleClose() {
        if (!isSaving) {
            props.onClose();
        }
    }

    function buildSurveyEmailHtml() {
        const safeTitle = escapeHtml(
            props.surveyInfo.title || "Untitled survey"
        );
        const messageSection = messageRef.current
            ? `<tr>
                <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #555555;">
                    ${escapeHtml(messageRef.current).replace(/\n/g, "<br>")}
                </td>
               </tr>`
            : "";
        const link =
            "https://nodiant.faw.cymru/surveys/r?key=" +
            encodeURIComponent(props.surveyInfo.key);

        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
        <tr>
            <td align="center" style="padding: 20px 0;">
                <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <tr>
                        <td style="background-color: rgb(0, 149, 61); padding: 20px 30px;">
                            <h1 style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 22px; color: #ffffff;">Nodiant Online</h1>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 30px 30px 10px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #333333;">
                            A survey has been shared with you:
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: bold; color: #333333;">
                            ${safeTitle}
                        </td>
                    </tr>
                    ${messageSection}
                    <tr>
                        <td style="padding: 0 30px 30px 30px;">
                            <a href="${link}" style="display: inline-block; padding: 12px 24px; background-color: rgb(0, 149, 61); color: #ffffff; text-decoration: none; border-radius: 4px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold;">Take survey</a>
                        </td>
                    </tr>
                    <tr>
                        <td style="background-color: #ffffff; border-top: 1px solid #e0e0e0; padding: 15px 30px; text-align: center;">
                            <img src="https://firebasestorage.googleapis.com/v0/b/nodiant-e20a8.appspot.com/o/FAW_CLEAR.png?alt=media&amp;token=a5b922df-5982-4fd9-af85-5609bb390d55" alt="FAW Logo" height="40" style="height: 40px;">
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
    }

    function sendSurveyEmails(notificationDate) {
        // Same delivery path as the playlist, timeline and project dialogs:
        // uids go to sendUserNotification and addresses stay server-side.
        return sendSharedItemEmails({
            embargoNotifications,
            firebase,
            html: buildSurveyEmailHtml(),
            label: `survey: ${props.surveyInfo.title || "Untitled survey"}`,
            notificationDate,
            recipients,
            subject: "Nodiant Online - New Survey Available",
        });
    }

    // Nobody being told is only a mistake worth interrupting for when there is
    // somebody to tell.
    const wouldShareSilently =
        recipients.length > 0 && !sendNotifications && !sendEmails;

    function handleShareClick() {
        if (wouldShareSilently) {
            setSilentShareConfirmOpen(true);
            return;
        }

        handleShare();
    }

    function handleSilentShareCancel() {
        setSilentShareConfirmOpen(false);
        handleReviewDeliveryOptions();
    }

    function handleSilentShareConfirm() {
        setSilentShareConfirmOpen(false);
        handleShare();
    }

    async function handleShare() {
        const usersObject = {};
        selectedUsers.forEach((user) => {
            usersObject[user.key] = {
                canEdit: Boolean(user.canEdit),
                canViewResults: Boolean(user.canViewResults),
            };
        });

        const groupsObject = {};
        selectedGroups.forEach((group) => {
            groupsObject[group.key] = {
                canEdit: Boolean(group.canEdit),
                canViewResults: Boolean(group.canViewResults),
            };
        });

        const notificationDate = embargoNotifications ? fromDate : new Date();
        const tasks = [
            firebase
                .database()
                .ref("surveyinfo")
                .child(props.surveyInfo.key)
                .update({ users: usersObject, groups: groupsObject }),
        ];

        if (sendNotifications && recipients.length > 0) {
            tasks.push(
                firebase
                    .database()
                    .ref("notifications")
                    .child(props.surveyInfo.key)
                    .set({
                        title: props.surveyInfo.title || "Untitled survey",
                        type: "survey",
                        users: jsonFromArray(recipients),
                        message: messageRef.current,
                        date: dayjs().toString(),
                        fromDate: dayjs(notificationDate).toString(),
                    })
            );
        }

        if (sendEmails) {
            tasks.push(sendSurveyEmails(notificationDate));
        }

        setIsSaving(true);
        setSaveError("");

        try {
            await Promise.all(tasks);

            enqueueSnackbar(
                buildShareFeedbackMessage({
                    assetLabel: "Survey",
                    userCount: selectedUsers.length,
                    groupCount: selectedGroups.length,
                    sendNotifications,
                    sendEmails,
                    embargoNotifications,
                    notificationDate,
                }),
                {
                    variant: "success",
                    autoHideDuration: 4500,
                }
            );

            messageRef.current = "";
            props.onClose(selectedUsers.length, selectedGroups.length);
        } catch (error) {
            console.error("Unable to share survey", error);
            setSaveError(
                "The survey could not be shared. Please check your connection and try again."
            );
            setIsSaving(false);
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="share-survey-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="share-survey-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Share survey
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the groups and individual users who can access
                        this survey.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close sharing"
                    onClick={handleClose}
                    disabled={isSaving}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {isLoading ? (
                    <Box
                        role="status"
                        sx={{
                            minHeight: 360,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress
                            size={32}
                            aria-label="Loading sharing options"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading sharing options…
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "grid", gap: 2 }}>
                        {saveError && (
                            <Alert severity="error">{saveError}</Alert>
                        )}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1,
                            }}
                        >
                            {[
                                {
                                    icon: <GroupsOutlinedIcon />,
                                    label: "Groups",
                                    value: selectedGroups.length,
                                    // Counted across groups, so anyone in more
                                    // than one selected group is counted once
                                    caption: `Containing ${
                                        groupCoverage.memberKeys.size
                                    } ${
                                        groupCoverage.memberKeys.size === 1
                                            ? "user"
                                            : "users"
                                    }`,
                                },
                                {
                                    icon: <PersonOutlineIcon />,
                                    label: "Individual users",
                                    value: directlySelectedUsers.length,
                                },
                                {
                                    icon: <ShareOutlinedIcon />,
                                    label: "Recipients",
                                    value: recipients.length,
                                },
                            ].map((summary) => (
                                <Box
                                    key={summary.label}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: "primary.main",
                                            display: "grid",
                                        }}
                                    >
                                        {summary.icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {summary.label}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{ lineHeight: 1 }}
                                        >
                                            {summary.value}
                                        </Typography>
                                        {summary.caption && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    mt: 0.4,
                                                }}
                                            >
                                                {summary.caption}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Paper variant="outlined" sx={surfaceSx}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    People with access
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Everyone listed can take the survey. Grant
                                    editing or answer access where needed.
                                </Typography>
                            </Box>
                            <Divider />
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Tab
                                    icon={<GroupsOutlinedIcon />}
                                    iconPosition="start"
                                    label={`Groups (${selectedGroups.length})`}
                                />
                                <Tab
                                    icon={<PersonOutlineIcon />}
                                    iconPosition="start"
                                    label={`Users (${directlySelectedUsers.length})`}
                                />
                            </Tabs>

                            <Box
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "minmax(0, 1fr)",
                                        md: "repeat(2, minmax(0, 1fr))",
                                    },
                                    gap: 2,
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                {activeTab === 0 ? (
                                    <>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Selected groups"
                                                count={selectedGroups.length}
                                            />
                                            <TextField
                                                label="Search selected groups"
                                                size="small"
                                                fullWidth
                                                value={selectedGroupFilter}
                                                onChange={(event) =>
                                                    setSelectedGroupFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            <List
                                                disablePadding
                                                sx={{
                                                    height: { md: 390 },
                                                    maxHeight: {
                                                        xs: 300,
                                                        md: 390,
                                                    },
                                                    overflowY: "auto",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {selectedGroupOptions.length ===
                                                0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No groups currently have access."
                                                            primaryTypographyProps={{
                                                                color: "text.secondary",
                                                                variant:
                                                                    "body2",
                                                            }}
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    selectedGroupOptions.map(
                                                        (
                                                            { group, hub },
                                                            index
                                                        ) => (
                                                            <React.Fragment
                                                                key={group.key}
                                                            >
                                                                {(index === 0 ||
                                                                    selectedGroupOptions[
                                                                        index -
                                                                            1
                                                                    ].hub
                                                                        ?.key !==
                                                                        hub?.key) && (
                                                                    <ShareDialogGroupHeader
                                                                        breadcrumb={getHubBreadcrumb(
                                                                            hub
                                                                        )}
                                                                    />
                                                                )}
                                                                <AccessRow
                                                                    avatar={
                                                                        <ShareDialogGroupIcon />
                                                                    }
                                                                    title={
                                                                        group.name
                                                                    }
                                                                    secondary={`${getHubBreadcrumb(hub)} · ${
                                                                        (
                                                                            group.users ||
                                                                            []
                                                                        ).length
                                                                    } ${
                                                                        (
                                                                            group.users ||
                                                                            []
                                                                        )
                                                                            .length ===
                                                                        1
                                                                            ? "member"
                                                                            : "members"
                                                                    }`}
                                                                    canEdit={
                                                                        group.canEdit
                                                                    }
                                                                    canViewResults={
                                                                        group.canViewResults
                                                                    }
                                                                    onViewMembers={handleViewGroupMembers(
                                                                        group,
                                                                        hub
                                                                    )}
                                                                    onChangePermission={(
                                                                        permission
                                                                    ) =>
                                                                        updateGroupPermission(
                                                                            group.key,
                                                                            permission
                                                                        )
                                                                    }
                                                                    onRemove={() =>
                                                                        setSelectedGroups(
                                                                            (
                                                                                current
                                                                            ) =>
                                                                                current.filter(
                                                                                    (
                                                                                        item
                                                                                    ) =>
                                                                                        item.key !==
                                                                                        group.key
                                                                                )
                                                                        )
                                                                    }
                                                                />
                                                                {index <
                                                                    selectedGroupOptions.length -
                                                                        1 &&
                                                                    selectedGroupOptions[
                                                                        index +
                                                                            1
                                                                    ].hub
                                                                        ?.key ===
                                                                        hub?.key && (
                                                                        <Divider />
                                                                    )}
                                                            </React.Fragment>
                                                        )
                                                    )
                                                )}
                                            </List>
                                        </Paper>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Available groups"
                                                count={filteredAvailableHubs.reduce(
                                                    (count, hub) =>
                                                        count +
                                                        hub.groups.length,
                                                    0
                                                )}
                                            />
                                            <TextField
                                                label="Search available groups"
                                                size="small"
                                                fullWidth
                                                value={availableGroupFilter}
                                                onChange={(event) =>
                                                    setAvailableGroupFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            <List
                                                disablePadding
                                                sx={{
                                                    height: { md: 390 },
                                                    maxHeight: {
                                                        xs: 300,
                                                        md: 390,
                                                    },
                                                    overflowY: "auto",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {filteredAvailableHubs.length ===
                                                0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No matching groups are available."
                                                            primaryTypographyProps={{
                                                                color: "text.secondary",
                                                                variant:
                                                                    "body2",
                                                            }}
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    filteredAvailableHubs.map(
                                                        (hub) => (
                                                            <React.Fragment
                                                                key={hub.key}
                                                            >
                                                                <ShareDialogGroupHeader
                                                                    breadcrumb={getHubBreadcrumb(
                                                                        hub
                                                                    )}
                                                                />
                                                                {hub.groups.map(
                                                                    (
                                                                        group,
                                                                        index
                                                                    ) => (
                                                                        <ShareDialogAvailableGroupRow
                                                                            key={
                                                                                group.key
                                                                            }
                                                                            name={
                                                                                group.displayName ||
                                                                                group.name
                                                                            }
                                                                            memberCount={
                                                                                (
                                                                                    group.users ||
                                                                                    []
                                                                                )
                                                                                    .length
                                                                            }
                                                                            onClick={() =>
                                                                                addGroup(
                                                                                    group
                                                                                )
                                                                            }
                                                                            divided={
                                                                                index <
                                                                                hub
                                                                                    .groups
                                                                                    .length -
                                                                                    1
                                                                            }
                                                                        />
                                                                    )
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    )
                                                )}
                                            </List>
                                        </Paper>
                                    </>
                                ) : (
                                    <>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Selected users"
                                                count={
                                                    usersTabLoading
                                                        ? "—"
                                                        : directlySelectedUsers.length
                                                }
                                            />
                                            <TextField
                                                label="Search selected users"
                                                size="small"
                                                fullWidth
                                                value={selectedUserFilter}
                                                disabled={usersTabLoading}
                                                onChange={(event) =>
                                                    setSelectedUserFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            {usersTabLoading ? (
                                                <ShareDialogUserListLoading label="Loading selected users…" />
                                            ) : (
                                                <List
                                                    disablePadding
                                                    sx={{
                                                        height: { md: 390 },
                                                        maxHeight: {
                                                            xs: 300,
                                                            md: 390,
                                                        },
                                                        overflowY: "auto",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {selectedUserSections.length ===
                                                    0 ? (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="No users have been added individually."
                                                                primaryTypographyProps={{
                                                                    color: "text.secondary",
                                                                    variant:
                                                                        "body2",
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ) : (
                                                        selectedUserSections.map(
                                                            (section) => (
                                                                <React.Fragment
                                                                    key={
                                                                        section.key
                                                                    }
                                                                >
                                                                    {showSelectedUserSectionHeaders && (
                                                                        <ShareDialogUserSectionHeader
                                                                            icon={
                                                                                section.icon
                                                                            }
                                                                            label={
                                                                                section.label
                                                                            }
                                                                            count={
                                                                                section
                                                                                    .users
                                                                                    .length
                                                                            }
                                                                        />
                                                                    )}
                                                                    {section.users.map(
                                                                        (
                                                                            user,
                                                                            index
                                                                        ) =>
                                                                            section.viaGroup ? (
                                                                                <ShareDialogGroupUserRow
                                                                                    key={
                                                                                        user.key
                                                                                    }
                                                                                    user={
                                                                                        user
                                                                                    }
                                                                                    groupNames={groupCoverage.groupNamesByUserKey.get(
                                                                                        user.key
                                                                                    )}
                                                                                    roomy
                                                                                    divided={
                                                                                        index <
                                                                                        section
                                                                                            .users
                                                                                            .length -
                                                                                            1
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <React.Fragment
                                                                                    key={
                                                                                        user.key
                                                                                    }
                                                                                >
                                                                    <AccessRow
                                                                        avatar={
                                                                            <UserAvatar
                                                                                userOrHub={
                                                                                    user
                                                                                }
                                                                                path="users"
                                                                                variant="circular"
                                                                                size="medium"
                                                                            />
                                                                        }
                                                                        title={
                                                                            user.fullName
                                                                        }
                                                                        secondary={
                                                                            user.customRoleName ||
                                                                            "Individual access"
                                                                        }
                                                                        canEdit={
                                                                            user.canEdit
                                                                        }
                                                                        canViewResults={
                                                                            user.canViewResults
                                                                        }
                                                                        onChangePermission={(
                                                                            permission
                                                                        ) =>
                                                                            updateUserPermission(
                                                                                user.key,
                                                                                permission
                                                                            )
                                                                        }
                                                                        onRemove={() =>
                                                                            setSelectedUsers(
                                                                                (
                                                                                    current
                                                                                ) =>
                                                                                    current.filter(
                                                                                        (
                                                                                            item
                                                                                        ) =>
                                                                                            item.key !==
                                                                                            user.key
                                                                                    )
                                                                            )
                                                                        }
                                                                    />
                                                                    {index <
                                                                        section
                                                                            .users
                                                                            .length -
                                                                            1 && (
                                                                        <Divider />
                                                                    )}
                                                                                </React.Fragment>
                                                                            )
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )
                                                    )}
                                                </List>
                                            )}
                                        </Paper>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Available users"
                                                count={
                                                    usersTabLoading
                                                        ? "—"
                                                        : availableUsers.length
                                                }
                                            />
                                            <TextField
                                                label="Search available users"
                                                size="small"
                                                fullWidth
                                                value={userSearch}
                                                disabled={usersTabLoading}
                                                onChange={(event) =>
                                                    setUserSearch(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            {usersTabLoading ? (
                                                <ShareDialogUserListLoading label="Loading available users…" />
                                            ) : (
                                                <List
                                                    disablePadding
                                                    sx={{
                                                        height: { md: 390 },
                                                        maxHeight: {
                                                            xs: 300,
                                                            md: 390,
                                                        },
                                                        overflowY: "auto",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {availableUsers.length ===
                                                    0 ? (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="No matching users are available."
                                                                primaryTypographyProps={{
                                                                    color: "text.secondary",
                                                                    variant:
                                                                        "body2",
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ) : (
                                                        availableUsers.map(
                                                            (user, index) => (
                                                                <React.Fragment
                                                                    key={
                                                                        user.key
                                                                    }
                                                                >
                                                                    <ListItemButton
                                                                        onClick={() =>
                                                                            addUser(
                                                                                user
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            gap: 1,
                                                                            px: 1.25,
                                                                            py: 0.65,
                                                                        }}
                                                                    >
                                                                        <ListItemAvatar>
                                                                            <UserAvatar
                                                                                userOrHub={
                                                                                    user
                                                                                }
                                                                                path="users"
                                                                                variant="circular"
                                                                                size="small"
                                                                            />
                                                                        </ListItemAvatar>
                                                                        <ListItemText
                                                                            primary={
                                                                                user.fullName
                                                                            }
                                                                            secondary={
                                                                                user.customRoleName
                                                                            }
                                                                            primaryTypographyProps={{
                                                                                variant:
                                                                                    "body2",
                                                                                fontWeight: 800,
                                                                            }}
                                                                        />
                                                                        <AddCircleOutlineIcon
                                                                            fontSize="small"
                                                                            sx={{
                                                                                color: "primary.main",
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                    {index <
                                                                        availableUsers.length -
                                                                            1 && (
                                                                        <Divider />
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )
                                                    )}
                                                </List>
                                            )}
                                        </Paper>
                                    </>
                                )}
                            </Box>
                        </Paper>

                        <Box ref={deliveryOptionsRef}>
                            <ShareDialogDeliveryOptions
                                assetType="survey"
                                sendNotifications={sendNotifications}
                                sendEmails={sendEmails}
                                embargoNotifications={embargoNotifications}
                                message={messageRef.current}
                                messageResetKey={props.open}
                                fromDate={fromDate}
                                onDeliveryChange={handleDeliveryChange}
                                onMessageChange={(value) => {
                                    messageRef.current = value;
                                }}
                                onDateChange={setFromDate}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                {!isLoading && (
                    <ShareDialogDeliverySummary
                        sendNotifications={sendNotifications}
                        sendEmails={sendEmails}
                        embargoNotifications={embargoNotifications}
                        fromDate={fromDate}
                        recipientCount={recipients.length}
                        onReview={handleReviewDeliveryOptions}
                    />
                )}
                <Button
                    color="inherit"
                    onClick={handleClose}
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<ShareOutlinedIcon />}
                    onClick={handleShareClick}
                    disabled={isLoading || isSaving}
                >
                    {isSaving ? "Sharing…" : "Share survey"}
                </Button>
            </DialogActions>
            {isSaving && <LinearProgress />}

            <ShareDialogGroupMembersDialog
                open={viewingGroup !== null}
                groupName={viewingGroup ? viewingGroup.group.name : ""}
                breadcrumb={
                    viewingGroup ? getHubBreadcrumb(viewingGroup.hub) : ""
                }
                members={viewedGroupMembers}
                onClose={handleGroupMembersClose}
            />

            <ShareDialogSilentShareConfirm
                open={silentShareConfirmOpen}
                assetType="survey"
                recipientCount={recipients.length}
                onCancel={handleSilentShareCancel}
                onConfirm={handleSilentShareConfirm}
            />
        </Dialog>
    );
}
